<script setup>
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAccountTimeZone } from 'dashboard/composables/useAccountTimeZone';
import { isAccountModuleEnabled } from 'dashboard/helper/accountModules';
import { getDefaultConditions } from 'dashboard/helper/automationHelper';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import AutomationDateSimulation from './AutomationDateSimulation.vue';
import {
  CALENDAR_DATE_SOURCE,
  COMMEMORATIVE_KEYS,
  CONDITION_EVENT_BY_SUBJECT,
  CRM_OCCASION_SOURCE,
  CUSTOM_CALENDAR_DATE,
  DATE_SOURCES,
  DATE_TRIGGER_TEMPLATES,
  DATE_TRIGGER_MODULES,
  DATE_TRIGGER_SUBJECTS,
  DEFAULT_OCCASION_TYPE,
  DEFAULT_PAST_BUYERS_WINDOW_DAYS,
  MAX_CALENDAR_DAY,
  MAX_OFFSET_DAYS,
  MAX_PAST_BUYERS_WINDOW_DAYS,
  MIN_OFFSET_DAYS,
  MIN_PAST_BUYERS_WINDOW_DAYS,
  MONTHS,
  OCCASION_TYPES,
  OFFSET_MODES,
  RECURRENCES,
  RUN_HOURS,
  SUBJECT_BY_SOURCE,
  isCrmModuleEnabled,
  isRecurringSource,
  offsetDaysFor,
  offsetModeFor,
} from '../dateTrigger';

const props = defineProps({
  // Catálogo de condições do alvo (conversation_updated ou kanban_task_overdue), montado pelo formulário.
  filterTypes: {
    type: Array,
    default: () => [],
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
  showConditionBoardWarning: {
    type: Function,
    default: () => false,
  },
  // Modelos prontos: só na criação (e só com o módulo Clientes).
  showTemplates: {
    type: Boolean,
    default: false,
  },
  // Monta o corpo do simulate a partir do formulário; sem ele o botão Simular não aparece.
  getSimulationPayload: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['applyTemplate']);

const dateTrigger = defineModel('dateTrigger', {
  type: Object,
  required: true,
});
const conditions = defineModel('conditions', { type: Array, required: true });

const SOURCE_VALUE_SEPARATOR = ':';
// As fontes do CRM têm alvo contato, mas aparecem no grupo Clientes.
const CRM_GROUP = 'crm';
const SOURCE_GROUPS = [
  { key: DATE_TRIGGER_SUBJECTS.CONTACT, i18nKey: 'CONTACT' },
  { key: DATE_TRIGGER_SUBJECTS.CONVERSATION, i18nKey: 'CONVERSATION' },
  { key: DATE_TRIGGER_SUBJECTS.TASK, i18nKey: 'TASK' },
  { key: CRM_GROUP, i18nKey: 'CRM' },
];
const groupOf = source =>
  source.module === DATE_TRIGGER_MODULES.CRM ? CRM_GROUP : source.subject;

const { t, locale } = useI18n();
const { currentAccount, accountScopedRoute } = useAccount();
const { timeZone } = useAccountTimeZone();
const customAttributes = useMapGetter('attributes/getAttributes');
const labels = useMapGetter('labels/getLabels');
// Segmentos de contato (007), carregados pela barra lateral na store de customViews.
const contactSegments = useMapGetter('customViews/getContactCustomViews');
const conditionsRef = useTemplateRef('conditionsRef');
const isSimulationOpen = ref(false);
// Cada clique em Simular remonta o painel, que refaz a chamada com o formulário atual.
const simulationKey = ref(0);
const simulationReferenceDate = ref('');

const openSimulation = () => {
  simulationKey.value += 1;
  isSimulationOpen.value = true;
};

const isKanbanEnabled = computed(() =>
  isAccountModuleEnabled(currentAccount.value, DATE_TRIGGER_MODULES.KANBAN)
);

const isCrmEnabled = computed(() => isCrmModuleEnabled(currentAccount.value));

const templateOptions = computed(() =>
  props.showTemplates && isCrmEnabled.value ? DATE_TRIGGER_TEMPLATES : []
);

const MODULE_ENABLED = {
  [DATE_TRIGGER_MODULES.KANBAN]: isKanbanEnabled,
  [DATE_TRIGGER_MODULES.CRM]: isCrmEnabled,
};

const availableSources = computed(() =>
  DATE_SOURCES.filter(
    source => !source.module || MODULE_ENABLED[source.module].value
  )
);

const dateAttributesOf = attributeModel =>
  (customAttributes.value || []).filter(
    attribute =>
      attribute.attribute_model === attributeModel &&
      attribute.attribute_display_type === 'date'
  );

const toSourceValue = (sourceType, attributeKey) =>
  `${sourceType}${SOURCE_VALUE_SEPARATOR}${attributeKey || ''}`;

const optionsForSource = source => {
  if (source.attributeModel) {
    return dateAttributesOf(source.attributeModel).map(attribute => ({
      value: toSourceValue(source.key, attribute.attribute_key),
      label: attribute.attribute_display_name,
    }));
  }
  return [
    {
      value: toSourceValue(source.key),
      label: t(
        `AUTOMATION.DATE_TRIGGER.SOURCE.OPTIONS.${source.key.toUpperCase()}`
      ),
    },
  ];
};

const sourceGroups = computed(() =>
  SOURCE_GROUPS.map(group => ({
    ...group,
    options: availableSources.value
      .filter(source => groupOf(source) === group.key)
      .flatMap(optionsForSource),
  })).filter(group => group.options.length)
);

// Grupos viram cabeçalhos não selecionáveis do FilterSelect.
const sourceOptions = computed(() =>
  sourceGroups.value.flatMap(group => [
    {
      value: `group${SOURCE_VALUE_SEPARATOR}${group.key}`,
      label: t(`AUTOMATION.DATE_TRIGGER.SOURCE.GROUPS.${group.i18nKey}`),
      disabled: true,
    },
    ...group.options,
  ])
);

const hasDateAttributes = computed(() =>
  availableSources.value.some(
    source =>
      source.attributeModel && dateAttributesOf(source.attributeModel).length
  )
);

const attributesRoute = computed(() => accountScopedRoute('attributes_list'));

const subject = computed(
  () => SUBJECT_BY_SOURCE[dateTrigger.value.source_type] || null
);

const updateDateTrigger = patch => {
  dateTrigger.value = { ...dateTrigger.value, ...patch };
};

const isCrmOccasion = computed(
  () => dateTrigger.value.source_type === CRM_OCCASION_SOURCE
);
const isCalendarDate = computed(
  () => dateTrigger.value.source_type === CALENDAR_DATE_SOURCE
);

const initialOffset = dateTrigger.value.offset_days;
// Automação salva em Datas do cliente com offset null usa a antecedência de cada data.
const initialOffsetMode = () => {
  if (initialOffset === null && isCrmOccasion.value) {
    return OFFSET_MODES.LEAD_DAYS;
  }
  return offsetModeFor(initialOffset ?? 0);
};
const offsetMode = ref(initialOffsetMode());
const offsetCount = ref(initialOffset ? Math.abs(initialOffset) : null);

// offset_mode fica no modelo da tela para a validação distinguir "antecedência" de N vazio;
// o payload não o leva (buildDateTriggerAttributes).
watch([offsetMode, offsetCount], ([mode, count]) => {
  updateDateTrigger({
    offset_mode: mode,
    offset_days: offsetDaysFor(mode, count),
  });
});

const sourceValue = computed({
  get: () =>
    dateTrigger.value.source_type
      ? toSourceValue(
          dateTrigger.value.source_type,
          dateTrigger.value.attribute_key
        )
      : '',
  set: value => {
    const [sourceType, attributeKey] = value.split(SOURCE_VALUE_SEPARATOR);
    const previousSubject = subject.value;
    updateDateTrigger({
      source_type: sourceType,
      attribute_key: attributeKey || null,
      recurrence: isRecurringSource(sourceType)
        ? dateTrigger.value.recurrence || RECURRENCES[0]
        : null,
      occasion_type:
        sourceType === CRM_OCCASION_SOURCE
          ? dateTrigger.value.occasion_type || DEFAULT_OCCASION_TYPE
          : dateTrigger.value.occasion_type,
      commemorative_key:
        sourceType === CALENDAR_DATE_SOURCE &&
        !dateTrigger.value.commemorative_key &&
        !dateTrigger.value.calendar_month
          ? COMMEMORATIVE_KEYS[0]
          : dateTrigger.value.commemorative_key,
    });
    // "Antecedência de cada data" só existe em Datas do cliente.
    if (
      sourceType !== CRM_OCCASION_SOURCE &&
      offsetMode.value === OFFSET_MODES.LEAD_DAYS
    ) {
      offsetMode.value = OFFSET_MODES.ON_DAY;
    }
    // Condições de conversa não valem para tarefa (e vice-versa); contato não tem condições.
    if (SUBJECT_BY_SOURCE[sourceType] !== previousSubject) {
      conditions.value = [];
    }
  },
});

const offsetOptions = computed(() =>
  [
    OFFSET_MODES.ON_DAY,
    OFFSET_MODES.BEFORE,
    OFFSET_MODES.AFTER,
    ...(isCrmOccasion.value ? [OFFSET_MODES.LEAD_DAYS] : []),
  ].map(mode => ({
    value: mode,
    label: t(`AUTOMATION.DATE_TRIGGER.OFFSET.OPTIONS.${mode.toUpperCase()}`),
  }))
);

const needsOffsetCount = computed(() =>
  [OFFSET_MODES.BEFORE, OFFSET_MODES.AFTER].includes(offsetMode.value)
);

const occasionTypeOptions = computed(() =>
  OCCASION_TYPES.map(type => ({
    value: type,
    label: t(
      `AUTOMATION.DATE_TRIGGER.CRM.OCCASION_TYPES.${type.toUpperCase()}`
    ),
  }))
);

const occasionType = computed({
  get: () => dateTrigger.value.occasion_type || DEFAULT_OCCASION_TYPE,
  set: value => updateDateTrigger({ occasion_type: value }),
});

const commemorativeOptions = computed(() => [
  ...COMMEMORATIVE_KEYS.map(key => ({
    value: key,
    label: t(`CRM_RECOMPRA.DATES.${key.toUpperCase()}`),
  })),
  { value: CUSTOM_CALENDAR_DATE, label: t('CRM_RECOMPRA.DATES.CUSTOM') },
]);

const commemorativeDate = computed({
  get: () => dateTrigger.value.commemorative_key || CUSTOM_CALENDAR_DATE,
  set: value =>
    updateDateTrigger({
      commemorative_key: value === CUSTOM_CALENDAR_DATE ? null : value,
    }),
});

const isCustomCalendarDate = computed(
  () => commemorativeDate.value === CUSTOM_CALENDAR_DATE
);

const monthOptions = computed(() => {
  const formatter = new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    month: 'long',
    timeZone: 'UTC',
  });
  return MONTHS.map(month => ({
    value: month,
    label: formatter.format(new Date(Date.UTC(2000, month - 1, 1))),
  }));
});

const calendarMonth = computed({
  get: () => dateTrigger.value.calendar_month ?? '',
  set: value => updateDateTrigger({ calendar_month: value }),
});

const calendarDay = computed({
  get: () => dateTrigger.value.calendar_day ?? '',
  set: value =>
    updateDateTrigger({ calendar_day: value === '' ? null : value }),
});

const onlyPastBuyers = computed({
  get: () =>
    dateTrigger.value.past_buyers_window_days !== null &&
    dateTrigger.value.past_buyers_window_days !== undefined,
  set: checked =>
    updateDateTrigger({
      past_buyers_window_days: checked ? DEFAULT_PAST_BUYERS_WINDOW_DAYS : null,
    }),
});

const pastBuyersWindowDays = computed({
  get: () => dateTrigger.value.past_buyers_window_days ?? '',
  set: value => updateDateTrigger({ past_buyers_window_days: value }),
});

const showRecurrence = computed(() =>
  isRecurringSource(dateTrigger.value.source_type)
);

const recurrenceOptions = computed(() =>
  RECURRENCES.map(recurrence => ({
    value: recurrence,
    label: t(
      `AUTOMATION.DATE_TRIGGER.RECURRENCE.OPTIONS.${recurrence.toUpperCase()}`
    ),
  }))
);

const recurrence = computed({
  get: () => dateTrigger.value.recurrence || RECURRENCES[0],
  set: value => updateDateTrigger({ recurrence: value }),
});

const runHourOptions = RUN_HOURS.map(hour => ({
  value: hour,
  label: `${String(hour).padStart(2, '0')}:00`,
}));

const runHour = computed({
  get: () => dateTrigger.value.run_hour,
  set: value => updateDateTrigger({ run_hour: value }),
});

const labelOptions = computed(() =>
  (labels.value || []).map(label => ({ id: label.id, name: label.title }))
);

// Opção vazia do seletor de segmento: custom_filter_id null.
const NO_SEGMENT = '';

const segmentOptions = computed(() => [
  {
    value: NO_SEGMENT,
    label: t('AUTOMATION.DATE_TRIGGER.AUDIENCE.SEGMENT_NONE'),
  },
  ...(contactSegments.value || []).map(segment => ({
    value: segment.id,
    label: segment.name,
  })),
]);

const segmentId = computed({
  get: () => dateTrigger.value.custom_filter_id ?? NO_SEGMENT,
  set: value =>
    updateDateTrigger({
      custom_filter_id: value === NO_SEGMENT ? null : value,
    }),
});

const audienceLabels = computed({
  get: () =>
    labelOptions.value.filter(option =>
      (dateTrigger.value.audience_label_ids || []).includes(option.id)
    ),
  set: selected =>
    updateDateTrigger({ audience_label_ids: selected.map(label => label.id) }),
});

const conditionEvent = computed(() =>
  subject.value ? CONDITION_EVENT_BY_SUBJECT[subject.value] : null
);
const showConditions = computed(() => Boolean(conditionEvent.value));

const DATE_ERROR_KEYS = [
  'date_source',
  'date_offset',
  'date_occasion',
  'date_calendar',
  'date_window',
];
const hasError = computed(() => DATE_ERROR_KEYS.some(key => props.errors[key]));

const appendCondition = () => {
  const hasBoardCondition = conditions.value.some(
    condition => condition.attribute_key === 'kanban_board_id'
  );
  const [defaultCondition] =
    subject.value === DATE_TRIGGER_SUBJECTS.TASK && hasBoardCondition
      ? [
          {
            ...getDefaultConditions(conditionEvent.value)[0],
            attribute_key: 'kanban_step_id',
          },
        ]
      : getDefaultConditions(conditionEvent.value);
  conditions.value = [...conditions.value, defaultCondition];
};

// Na automação por data as condições são opcionais: a última também sai.
const removeCondition = index => {
  conditions.value = conditions.value.filter((_, i) => i !== index);
};

const validate = () =>
  (conditionsRef.value || []).every(condition => condition.validate());

const resetValidation = () => {
  conditionsRef.value?.forEach(condition => condition.resetValidation());
};

defineExpose({ validate, resetValidation });
</script>

<template>
  <div class="flex flex-col min-w-0 gap-2">
    <label class="mb-0">
      {{ $t('AUTOMATION.DATE_TRIGGER.LABEL') }}
    </label>
    <div
      class="flex flex-col min-w-0 gap-3 p-4 outline outline-1 -outline-offset-1 rounded-xl"
      :class="
        hasError
          ? 'outline-n-ruby-5 bg-n-ruby-2/50'
          : 'outline-n-weak dark:outline-n-strong'
      "
    >
      <div
        v-if="templateOptions.length"
        data-test="date-templates"
        class="flex flex-col gap-2 pb-3 border-b border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.TEMPLATES.LABEL') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <NextButton
            v-for="template in templateOptions"
            :key="template.key"
            :data-test="`date-template-${template.key}`"
            :icon="template.icon"
            :label="
              $t(
                `AUTOMATION.DATE_TRIGGER.TEMPLATES.${template.key.toUpperCase()}.NAME`
              )
            "
            slate
            faded
            sm
            @click="emit('applyTemplate', template.key)"
          />
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-3 min-h-8">
        <span class="text-sm w-36 shrink-0 text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.SOURCE.LABEL') }}
        </span>
        <FilterSelect
          v-model="sourceValue"
          data-test="date-source"
          :options="sourceOptions"
          :label="
            sourceValue
              ? null
              : $t('AUTOMATION.DATE_TRIGGER.SOURCE.PLACEHOLDER')
          "
        />
      </div>
      <p
        v-if="!hasDateAttributes"
        data-test="no-date-fields"
        class="flex items-start gap-2 p-3 mb-0 text-xs rounded-xl bg-n-alpha-1 text-n-slate-11"
      >
        <Icon icon="i-lucide-info" class="mt-0.5 shrink-0 text-n-slate-10" />
        <span>
          {{ $t('AUTOMATION.DATE_TRIGGER.SOURCE.NO_DATE_FIELDS') }}
          <router-link :to="attributesRoute" class="text-n-blue-11 underline">
            {{ $t('AUTOMATION.DATE_TRIGGER.SOURCE.NO_DATE_FIELDS_LINK') }}
          </router-link>
        </span>
      </p>
      <span v-if="errors.date_source" class="text-xs text-n-ruby-9">
        {{ $t('AUTOMATION.DATE_TRIGGER.SOURCE.ERROR') }}
      </span>

      <div
        v-if="isCrmOccasion"
        data-test="crm-occasion"
        class="flex flex-wrap items-center gap-3 min-h-8"
      >
        <span class="text-sm w-36 shrink-0 text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.CRM.OCCASION_TYPE_LABEL') }}
        </span>
        <FilterSelect
          v-model="occasionType"
          data-test="occasion-type"
          :options="occasionTypeOptions"
        />
      </div>
      <span v-if="errors.date_occasion" class="text-xs text-n-ruby-9">
        {{ $t('AUTOMATION.DATE_TRIGGER.CRM.OCCASION_TYPE_ERROR') }}
      </span>

      <template v-if="isCalendarDate">
        <div
          data-test="calendar-date"
          class="flex flex-wrap items-center gap-3 min-h-8"
        >
          <span class="text-sm w-36 shrink-0 text-n-slate-11">
            {{ $t('AUTOMATION.DATE_TRIGGER.CRM.COMMEMORATIVE_LABEL') }}
          </span>
          <FilterSelect
            v-model="commemorativeDate"
            data-test="commemorative-date"
            :options="commemorativeOptions"
          />
          <template v-if="isCustomCalendarDate">
            <FilterSelect
              v-model="calendarMonth"
              data-test="calendar-month"
              :options="monthOptions"
              :label="
                calendarMonth
                  ? null
                  : $t('AUTOMATION.DATE_TRIGGER.CRM.MONTH_PLACEHOLDER')
              "
            />
            <div class="w-20">
              <Input
                v-model="calendarDay"
                data-test="calendar-day"
                type="number"
                min="1"
                :max="String(MAX_CALENDAR_DAY)"
                :placeholder="$t('AUTOMATION.DATE_TRIGGER.CRM.DAY_PLACEHOLDER')"
              />
            </div>
          </template>
        </div>
        <span v-if="errors.date_calendar" class="text-xs text-n-ruby-9">
          {{ $t('AUTOMATION.DATE_TRIGGER.CRM.CALENDAR_ERROR') }}
        </span>
        <div class="flex flex-wrap items-center gap-3 min-h-8">
          <label class="flex items-center gap-2 mb-0 text-sm text-n-slate-12">
            <Checkbox v-model="onlyPastBuyers" data-test="past-buyers" />
            {{ $t('AUTOMATION.DATE_TRIGGER.CRM.PAST_BUYERS_LABEL') }}
          </label>
          <template v-if="onlyPastBuyers">
            <div class="w-20">
              <Input
                v-model="pastBuyersWindowDays"
                data-test="past-buyers-window"
                type="number"
                :min="String(MIN_PAST_BUYERS_WINDOW_DAYS)"
                :max="String(MAX_PAST_BUYERS_WINDOW_DAYS)"
              />
            </div>
            <span class="text-xs text-n-slate-11">
              {{ $t('AUTOMATION.DATE_TRIGGER.CRM.PAST_BUYERS_WINDOW_HINT') }}
            </span>
          </template>
        </div>
        <span v-if="errors.date_window" class="text-xs text-n-ruby-9">
          {{ $t('AUTOMATION.DATE_TRIGGER.CRM.PAST_BUYERS_WINDOW_ERROR') }}
        </span>
      </template>

      <div class="flex flex-wrap items-center gap-3 min-h-8">
        <span class="text-sm w-36 shrink-0 text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.OFFSET.LABEL') }}
        </span>
        <FilterSelect
          v-model="offsetMode"
          data-test="offset-mode"
          :options="offsetOptions"
        />
        <div v-if="needsOffsetCount" class="w-24">
          <Input
            v-model="offsetCount"
            data-test="offset-count"
            type="number"
            :min="String(MIN_OFFSET_DAYS)"
            :max="String(MAX_OFFSET_DAYS)"
          />
        </div>
      </div>
      <span v-if="errors.date_offset" class="text-xs text-n-ruby-9">
        {{ $t('AUTOMATION.DATE_TRIGGER.OFFSET.ERROR') }}
      </span>

      <div
        v-if="showRecurrence"
        data-test="recurrence"
        class="flex flex-wrap items-center gap-3 min-h-8"
      >
        <span class="text-sm w-36 shrink-0 text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.RECURRENCE.LABEL') }}
        </span>
        <FilterSelect v-model="recurrence" :options="recurrenceOptions" />
      </div>

      <div class="flex flex-wrap items-center gap-3 min-h-8">
        <span class="text-sm w-36 shrink-0 text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.RUN_HOUR.LABEL') }}
        </span>
        <FilterSelect
          v-model="runHour"
          data-test="run-hour"
          :options="runHourOptions"
        />
        <span data-test="time-zone-hint" class="text-xs text-n-slate-11">
          {{
            $t('AUTOMATION.DATE_TRIGGER.RUN_HOUR.TIME_ZONE_HINT', { timeZone })
          }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-3 min-h-8">
        <span class="text-sm w-36 shrink-0 text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.AUDIENCE.LABEL') }}
        </span>
        <MultiSelect v-model="audienceLabels" :options="labelOptions" />
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('AUTOMATION.DATE_TRIGGER.AUDIENCE.HINT') }}
      </p>
      <div class="flex flex-wrap items-center gap-3 min-h-8">
        <span class="text-sm w-36 shrink-0 text-n-slate-11">
          {{ $t('AUTOMATION.DATE_TRIGGER.AUDIENCE.SEGMENT_LABEL') }}
        </span>
        <FilterSelect
          v-model="segmentId"
          data-test="audience-segment"
          :options="segmentOptions"
        />
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('AUTOMATION.DATE_TRIGGER.AUDIENCE.SEGMENT_HINT') }}
      </p>

      <div
        v-if="showConditions"
        data-test="date-conditions"
        class="flex flex-col gap-3 pt-3 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
        </span>
        <template v-for="(condition, i) in conditions" :key="i">
          <ConditionRow
            ref="conditionsRef"
            v-model:attribute-key="conditions[i].attribute_key"
            v-model:filter-operator="conditions[i].filter_operator"
            v-model:values="conditions[i].values"
            v-model:query-operator="
              conditions[i > 0 ? i - 1 : 0].query_operator
            "
            :filter-types="filterTypes"
            :show-board-warning="showConditionBoardWarning(condition)"
            :board-warning-message="$t('AUTOMATION.KANBAN.SELECT_BOARD_FIRST')"
            :show-query-operator="i > 0"
            @remove="removeCondition(i)"
          />
        </template>
        <div>
          <NextButton
            icon="i-lucide-plus"
            blue
            faded
            sm
            :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
            @click="appendCondition"
          />
        </div>
      </div>

      <div v-if="getSimulationPayload" class="flex flex-col gap-3">
        <div>
          <NextButton
            data-test="simulate-button"
            icon="i-lucide-flask-conical"
            slate
            faded
            sm
            :label="$t('AUTOMATION.DATE_TRIGGER.SIMULATION.BUTTON')"
            @click="openSimulation"
          />
        </div>
        <AutomationDateSimulation
          v-if="isSimulationOpen"
          :key="simulationKey"
          v-model:reference-date="simulationReferenceDate"
          :get-payload="getSimulationPayload"
          @close="isSimulationOpen = false"
        />
      </div>
    </div>
  </div>
</template>
